// APL-11850 (v3 parity): the FS-01/FS-03 error page and boot spinner are STATIC
// — a lazy chunk here defeats the error UI when the network is blackholed at
// the exact moment the screen is needed (iPad AAC lockdown).
import { PageCenteredCustomSpinner } from '@/components/ap/PageCenteredSpinner';
import { RealtimeDBError } from '@/components/ap/realtimeDBError/RealtimeDBError';
import { resolveExperienceShellSkeleton } from '@/components/ap/skeletons';
import { AppConfig } from '@/config/appConfig';
import { useAppRegion, useCurrentUser, useEnabledFeatures } from '@/hooks/dataHelpers';
import { useRealtimeDbProviderName } from '@/hooks/realtimeDataHelpers';
import { useIsOnline } from '@/hooks/utils';
import { updateConfigHeaderForDemoUseStagingAI } from '@/lib/commonHelpers';
import { checkNativeMethodSupported, getAppType } from '@/lib/offline/OfflineAppHelper';
import {
  signInNativeFirestore,
  waitForNativePendingWritesSynced,
} from '@/lib/offline/offlineAppService';
import { axiosInstance } from '@/providers/dataProvider';
import { firestoreDataProvider } from '@/providers/dataProvider/firestoreDataProvider';
import { supabaseDataProvider } from '@/providers/dataProvider/supabaseDataProvider';
import {
  FirebaseServices,
  initializeFirebaseApp,
} from '@/providers/realtimeDbProvider/firebaseServices';
import { probeFirestoreAccess } from '@/providers/realtimeDbProvider/firestoreAccessProbe';
import {
  initializeSupabaseApp,
  SupabaseServices,
} from '@/providers/supabaseProvider/supabaseServices';
import * as Sentry from '@sentry/react';
import { User } from '@supabase/supabase-js';
import { AuthError, UserCredential } from 'firebase/auth';
import { FirestoreError } from 'firebase/firestore';
import { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router';
// import { realtimeProviderStore } from "@/store/realtimeProviderStore";

/** R2-G62: while the realtime client signs in, exam surfaces keep the SAME
 *  full-screen shell skeleton the route boundary showed (take vs manage by
 *  path) instead of swapping to a centered spinner. Non-exam mounts (audit
 *  logs, AI usage, live map, connectivity) keep the spinner — they render
 *  inside their own page chrome where a shell skeleton would be wrong.
 *  Exported for the loading-branch pin only. */
export const RealtimePendingFallback = () => {
  const { pathname } = useLocation();
  return resolveExperienceShellSkeleton(pathname) ?? <PageCenteredCustomSpinner />;
};

// APL-11850 (browser-evidenced): a held identitytoolkit/securetoken request
// wedged signInWithCustomToken past 130s — the SDK has no built-in timeout, so
// the sign-in await gets an explicit bound. Same 30s bound as the Firestore
// probe: auth is a token mint + two auth-host round-trips, so it is legitimately
// slower than one Firestore read, and on iPad both share the same post-lockdown
// network settle. Exported for the sign-in-timeout pins only.
export const FIREBASE_SIGN_IN_TIMEOUT_MS = 30_000;

export const RealtimeContext = createContext({});

export const FirebaseRealtimeContext = createContext({});
interface UserProfile {
  id: string;
  displayName?: string;
  email?: string;
  // Add other profile fields as needed
  [key: string]: any;
}

interface UseFirestoreUserByTokenReturn {
  // user: UserProfile | null;
  firebaseUser: UserCredential['user'] | null;
  loading: boolean;
  error: AuthError | FirestoreError | Error | null;
  retry: () => void; // Function to allow retrying the operation
  firebaseServices: FirebaseServices | null;
}

/**
 * Custom React hook to authenticate with Firebase using a custom token
 * and then fetch the user's profile from Firestore.
 * If a token is not provided, it can optionally fetch one from a specified endpoint.
 *
 * @param providedToken The custom JWT token for Firebase authentication. If null, the hook might try to fetch one.
 * @param customTokenEndpoint Optional. The API endpoint to fetch the custom token from if `providedToken` is null.
 * The endpoint is expected to return a JSON object like { "token": "your_jwt_token" }.
 * @returns An object containing the user profile, loading state, error state, and a retry function.
 */
const getRealtimeDbToken = async (extraParams: any = {}, enabledFeatures: any = {}) => {
  try {
    let response;
    // if the feature is enabled, use the go service to fetch the token. Otherwise use the old endpoint only for supabase(china) region.
    if (enabledFeatures.get_realtimedb_token_from_go_service && AppConfig.mode !== 'development') {
      let config = {
        headers: {},
      };
      config = updateConfigHeaderForDemoUseStagingAI(config);
      response = await axiosInstance.post(
        `${AppConfig.aiServiceUrlOld}/auth/custom-fs-token`,
        {},
        config
        // config
      );
    } else {
      response = await axiosInstance.get(`${AppConfig.apiUrl}/get_realtimedb_token.json`, {
        params: extraParams,
      });
    }

    if (response?.status != 200) {
      throw new Error('Failed to fetch token: ' + response?.statusText);
    }
    const token = response?.data?.token;
    return token;
  } catch (error) {
    Sentry.captureException(error);
    throw error;
  }
};

// const useRealtimeDbDataProvider = (dataProviderName?: string) => {
//   const appRegion = useAppRegion();
//   if (appRegion === 'global' || dataProviderName === 'firestore') {
//     return firestoreDataProvider(firebaseServicesMain);
//   }
//   return null;
//   // } else {

//   //   // return realtimeDbDataProvider;
//   // }
//   // else return supabaseDataProvider
//   // const dataProvider = firestoreDataProvider(firebaseServices);
//   // return dataProvider;
// };
// Define the return type for useFirebaseUser more precisely
// const useUserPresence = ({
//   resource,
//   dataProviderName = 'firestore',
//   meta = {},
// }: {
//   resource: string;
//   dataProviderName?: string;
//   meta?: any;
// }) => {
//   const [presenceData, setPresenceData] = useState<any>({});
//   const [loading, setLoading] = useState(false);
//   const [error, setError] = useState<Error | null>(null);
//   const selectedDataProvider = useRealtimeDbDataProvider(dataProviderName);
//   useEffect(() => {
//     const presenceData = selectedDataProvider.getPresenceData({ resource, meta });
//     // setPresenceData(presenceData);
//   }, [dataProviderName]);

//   const handleRealtimeEvent = useCallback((event: string, data: any) => {
//     console.log('handleRealtimeEvent', event, data);
//     // if (event === 'PRESENCE_DATA') {
//     //   setPresenceData(data);
//     // }
//   }, []);

//   const handlePresenceData = useCallback(async () => {
//     // console.log('handlePresenceData', data);
//     // setPresenceData(data);
//     setLoading(true);
//     let unsubscribe: (() => void) | null;
//     try {
//       const response = await selectedDataProvider.getPresenceData({
//         resource,
//         meta: {
//           onRealtimeEvent: handleRealtimeEvent,
//           ...meta,
//         },
//       });
//       const { meta: responseMeta } = response;
//       // setPresenceData(presenceData);
//       unsubscribe = () => {
//         if (responseMeta?.unsubscribe) {
//           responseMeta.unsubscribe();
//         }
//       };
//     } catch (error: any) {
//       setError(error);
//     } finally {
//       setLoading(false);
//     }
//     return () => {
//       if (unsubscribe) {
//         unsubscribe();
//       }
//     };
//   }, []);

//   useEffect(() => {
//     if (!selectedDataProvider) {
//       return;
//     }
//     handlePresenceData();
//   }, [selectedDataProvider]);

//   // return { presenceData, loading, error };
// };

export const useFirebaseUser = ({
  providedToken,
  username,
  extraParams,
}: {
  providedToken: string | null;
  extraParams?: any;
  username?: string;
  presenceData?: any;
}): UseFirestoreUserByTokenReturn => {
  // State for the fetched Firestore user profile
  // State for the Firebase Auth user object
  const [firebaseUser, setFirebaseUser] = useState<UserCredential['user'] | null>(null);
  const currentUser = useCurrentUser();
  const enabledFeatures = useEnabledFeatures();
  // State for loading status for firebase user authentication and fetching
  const [loadingFirebaseUser, setLoadingFirebaseUser] = useState<boolean>(true); // Start as true
  // State for any errors encountered
  const [error, setError] = useState<AuthError | FirestoreError | Error | null>(null);
  // State to trigger a retry
  const [retryCount, setRetryCount] = useState(0);
  const { firebaseServices }: any = useFirebaseServicesCore();

  useEffect(() => {
    console.log('useEffect firebaseServices 1 ==> ', firebaseServices);
    if (!firebaseServices) {
      return;
    }

    console.log('useEffect firebaseServices 2 ==> ', firebaseServices);

    setLoadingFirebaseUser(true); // Set loading to true at the start of the effect

    const signInAndFetchFirestore = async (tokenToUse: string | null) => {
      let signInTimeoutId: ReturnType<typeof setTimeout> | undefined;
      try {
        // `ensureSignedInAs` owns the signout-then-signin decision: it no-ops when
        // the active session already matches `username`, and signs the previous
        // identity out before signing in when it differs (the View-As path).
        // Token fetch is bounded by the shared axios instance's 35s timeout
        // (dataProvider/utils/axios.ts:4-6; browser-verified Leg A: ECONNABORTED
        // → FS-01). The 30s race below deliberately covers only the sign-in stage.
        const token = tokenToUse ?? (await getRealtimeDbToken(extraParams, enabledFeatures));
        // APL-11641: native takes over the Firestore user_response writes once
        // signed in. signInNativeFirestore returns a name-tagged Error only on
        // a supported native build (Mac >= 13.0.0 / iOS >= 6.0.0) that couldn't sign
        // in, and null on every other build and on success — so block the student
        // with the FS-03 page on that error; they must not take a test whose
        // responses can't be saved.
        const nativeSignInError = await signInNativeFirestore(token);
        if (nativeSignInError) {
          setError(nativeSignInError);
          setFirebaseUser(null);
          return;
        }
        // Bounded sign-in (APL-11850): covers token persistence + web
        // signInWithCustomToken + the native path's webview follow-up. On
        // timeout → throw → FS-01 below. A late-resolving sign-in after the
        // timeout is intentionally ignored — nothing awaits it, so the error
        // state wins and FS-01's Refresh (retry) is the only recovery.
        const signInPromise = firebaseServices.ensureSignedInAs(token, username);
        signInPromise.catch(() => undefined); // dangling late rejection must not surface as unhandled
        const userCredential: any = await Promise.race([
          signInPromise,
          new Promise<never>((_, reject) => {
            signInTimeoutId = setTimeout(() => {
              const timeoutError = new Error(
                `Firebase sign-in timed out after ${FIREBASE_SIGN_IN_TIMEOUT_MS}ms`
              );
              timeoutError.name = 'FirebaseSignInTimeoutError';
              reject(timeoutError);
            }, FIREBASE_SIGN_IN_TIMEOUT_MS);
          }),
        ]);
        const authUser = userCredential?.user ?? userCredential ?? null;
        // APL-11850 — one bounded probe strictly AFTER sign-in. The whole
        // outcome decision lives inside probeFirestoreAccess (see its doc
        // block): it throws ONLY on an explicit `unavailable` (definite
        // "client is offline", Sentry FRONTEND-AP-V3-PROD-4MZ) → FS-01 via
        // the catch below; every other failure Sentry-warns internally and
        // resolves so boot proceeds.
        await probeFirestoreAccess(firebaseServices.firestoreDB);
        // Clear any error from a prior attempt — a transient native sign-in
        // failure (FS-03) or connection error must not survive a successful
        // retry, or the render gate (`error && blockOnStudentError`) keeps the
        // student blocked forever even though sign-in now succeeded (APL-11641).
        setError(null);
        setFirebaseUser(authUser);
      } catch (err: any) {
        console.error('Error during custom token sign-in or Firestore fetch:', err);
        Sentry.captureException(err);
        setError(err);
        setFirebaseUser(null);
      } finally {
        clearTimeout(signInTimeoutId);
        setLoadingFirebaseUser(false); // Authentication and fetching is complete
      }
    };

    signInAndFetchFirestore(providedToken);

    // No cleanup: Firebase signout is NOT done here. App-level teardown now lives
    // in the logout handler (authProvider.logout → firebaseSignout). Signing out
    // on effect cleanup raced re-mounts / identity switches and was a within-tab
    // session-loss source.
  }, [providedToken, retryCount, firebaseServices, username]);

  // APL-11641 / APL-11808 — pending-writes flush on the Student Experience mount.
  // NON-BLOCKING: the exam opens immediately (loading gates only on firebase auth).
  // This just kicks off a bounded 2-min background wait so native/web can finish
  // syncing pending writes; it never holds the loading spinner. Native and web both
  // run through the same bounded race. Multi-source hydration + its settle backstop
  // tolerate a source that hasn't fully synced yet. Keyed off `firebaseUser` so it
  // runs only after native has signed in via signInNativeFirestore.
  useEffect(() => {
    if (!firebaseServices || !firebaseUser || currentUser?.role !== 'student') {
      return;
    }

    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    const flushPendingWritesInBackground = async () => {
      // Native build polls native; every other build waits on the web Firestore SDK.
      const isNativeSupported = checkNativeMethodSupported(
        getAppType(),
        'getFirestorePendingWritesStatus'
      );
      try {
        // 2-minute deadline so the background wait can't run unbounded. Native-specific
        // name + message (APL-11808) so the Sentry issue reads as a native pending-writes
        // timeout — the app version / type are already on the event's device context.
        const timeoutPromise = new Promise((_, reject) => {
          timeoutId = setTimeout(() => {
            const timeoutError = new Error(
              'Native pending writes did not sync within 2 minutes via waitForNativePendingWritesSynced'
            );
            timeoutError.name = 'NativePendingWritesTimeout';
            reject(timeoutError);
          }, 120000);
        });

        const syncOperation = isNativeSupported
          ? waitForNativePendingWritesSynced()
          : firebaseServices.waitForPendingWrites();

        await Promise.race([syncOperation, timeoutPromise]);
      } catch (error) {
        // best-effort background flush; never blocks the exam. Only report the NATIVE
        // path (waitForNativePendingWritesSynced) — the web SDK's waitForPendingWrites
        // timing out is expected noise on older / non-native builds (APL-11808).
        if (isNativeSupported) {
          Sentry.captureException(error);
        }
      } finally {
        clearTimeout(timeoutId);
      }
    };

    flushPendingWritesInBackground();
  }, [firebaseServices, firebaseUser, currentUser?.role]);

  // APL-11808: loading gates ONLY on firebase auth now — the pending-writes wait is a
  // non-blocking background flush (see effect above), so the exam opens immediately
  // instead of holding a spinner up to 2 min while writes sync (native & web both).
  const loading = loadingFirebaseUser;

  const retry = () => {
    setRetryCount((prev) => prev + 1);
  };

  return { firebaseUser, loading, error, retry, firebaseServices };
};

// export const useFirebaseUser = ({ token }: { token: string }) => {
//   const firebaseServices = useFirebaseServices();
//   // const [user, setUser] = useState<UserProfile | null>(null);
//   const [firebaseUser, setFirebaseUser] = useState<UserCredential['user'] | null>(null);
//   // State for loading status
//   const [loading, setLoading] = useState<boolean>(false);
//   // State for any errors encountered
//   const [error, setError] = useState<AuthError | FirestoreError | Error | null>(null);
//   // State to trigger a retry
//   const [retryCount, setRetryCount] = useState(0);
//   useEffect(() => {
//     if (!firebaseServices) return;
//     // Async function to perform authentication and data fetching
//     const authenticateAndFetchUser = async () => {
//       if (!token) {
//         // setUser(null);
//         setFirebaseUser(null);
//         setLoading(false);
//         setError(null); // Clear error if token is removed
//         return;
//       }

//       setLoading(true);
//       setError(null);
//       // setUser(null);
//       setFirebaseUser(null);

//       try {
//         // Step 1: Authenticate with Firebase using the custom token
//         const userCredential = await signInWithCustomToken(firebaseServices.firebaseAuth, token);
//         const authUser = userCredential.user;
//         setFirebaseUser(authUser);

//         if (authUser && authUser.uid) {
//           // Step 2: Fetch the user's profile from Firestore
//           // const userDocRef = doc(db, firestoreCollectionName, authUser.uid);
//           // const docSnap = await getDoc(userDocRef);
//           // if (docSnap.exists()) {
//           //   // Set the user profile data
//           //   setUser({ id: docSnap.id, ...docSnap.data() } as UserProfile);
//           // } else {
//           //   // Handle case where Firestore document doesn't exist for the authenticated user
//           //   console.warn(`No Firestore document found for user UID: ${authUser.uid} in collection '${firestoreCollectionName}'`);
//           //   setError(new Error(`User profile not found in Firestore.`));
//           //   setUser(null); // Ensure user is null if profile not found
//           // }
//         } else {
//           // This case should ideally not happen if signInWithCustomToken succeeds
//           setError(new Error('Authentication succeeded but no user UID was found.'));
//         }
//       } catch (err: any) {
//         // Handle errors from authentication or Firestore
//         console.error('Error during custom token sign-in or Firestore fetch:', err);
//         setError(err);
//         // setUser(null);
//         setFirebaseUser(null);
//       } finally {
//         setLoading(false);
//       }
//     };

//     authenticateAndFetchUser();

//     // Cleanup function (optional, but good practice if you were setting up listeners)
//     // For signInWithCustomToken, direct cleanup of the auth state might involve signing out,
//     // but that's typically handled by other parts of the app logic (e.g., a logout button).
//     return () => {
//       // If you need to perform any cleanup when the token changes or component unmounts,
//       // you can do it here. For example, if you had a Firestore listener, you'd unsubscribe.
//     };
//   }, [token, retryCount, firebaseServices]); // Re-run effect if token, collectionName, or retryCount changes

//   // Function to manually trigger a retry
//   const retry = () => {
//     setRetryCount((prev) => prev + 1);
//   };

//   return { firebaseUser, loading, error, retry, firebaseServices };

//   // const firebaseUser = signInWithCustomToken(firebaseAuth, token);
//   // return firebaseUser;
// };

const useFirebaseServicesCore = () => {
  const [firebaseServices, setFirebaseServices] = useState<FirebaseServices | null>(null);
  useEffect(() => {
    const firestoreServices = initializeFirebaseApp();
    setFirebaseServices(firestoreServices);
  }, []);
  return { firebaseServices };
};

const useGetFirebaseOnlineStatus = ({
  firebaseServices,
}: {
  firebaseServices: FirebaseServices | null;
}) => {
  const [isFirebaseUserOnline, setIsFirebaseUserOnline] = useState(true);
  const handleRealtimeEvent = (event: string, data: any) => {
    console.log('firebaseOnlineStatus handleRealtimeEvent', event, data);
    setIsFirebaseUserOnline(data);
  };

  useEffect(() => {
    if (!firebaseServices) {
      return;
    }

    const dataProvider = firestoreDataProvider(firebaseServices);
    const getIsOnlineCallback = dataProvider.watchFirebaseUserOnline({
      meta: { onRealtimeEvent: handleRealtimeEvent },
    });
    return () => {
      if (getIsOnlineCallback?.meta?.unsubscribe) {
        getIsOnlineCallback.meta.unsubscribe();
      }
    };
  }, [handleRealtimeEvent, firebaseServices]);
  return { isFirebaseUserOnline };
};

export const useSetFirebaseUserPresence = ({
  user,
  resource,
  meta,
  firebaseServices,
}: {
  user: UserCredential['user'] | null;
  resource: string;
  meta?: any;
  firebaseServices: FirebaseServices | null;
}) => {
  const stringifiedMeta = JSON.stringify(meta);

  useEffect(() => {
    if (!user || !resource) {
      return;
    }
    console.log('useSetFirebaseUserPresence 390', user?.uid, resource);
    let unsubscribe: (() => void) | null;
    const setPresenceData = async () => {
      const dataProvider = firestoreDataProvider(firebaseServices);
      const response = await dataProvider.setPresenceData({
        resource,
        meta,
      });
      unsubscribe = response?.meta?.unsubscribe;
    };
    setPresenceData();
    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [stringifiedMeta, user?.uid, resource]);

  return null;
};

export type RealtimeClient = SupabaseServices & FirebaseServices;

type RealtimeServices = {
  appRegion: string;
  realtimeClient: RealtimeClient | null;
  user: UserCredential['user'] | null;
};

export const useRealtimeServices = (): RealtimeServices => {
  const dataProviderName = useRealtimeDbProviderName();
  const firebaseContext = useContext(FirebaseRealtimeContext);
  const supabaseContext = useContext(SupabaseRealtimeContext);
  if (dataProviderName === 'supabase') {
    return supabaseContext as RealtimeServices;
  }

  return firebaseContext as RealtimeServices;
};

const useSetSupabaseUserPresence = ({
  userId,
  resource,
  meta,
  supabaseServices,
}: {
  userId: string | null | undefined;
  resource: string;
  meta?: any;
  supabaseServices: SupabaseServices | null;
}) => {
  const stringifiedMeta = JSON.stringify(meta);

  useEffect(() => {
    if (!userId || !resource || !supabaseServices) {
      return;
    }

    let unsubscribe: (() => void) | null = null;

    const setPresenceData = async () => {
      const dataProvider = supabaseDataProvider(supabaseServices);
      const { meta: responseMeta } = await dataProvider.setPresenceData({
        resource,
        meta,
      });

      unsubscribe = responseMeta?.unsubscribe ?? null;
    };

    setPresenceData();

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [resource, stringifiedMeta, supabaseServices, userId]);
};

const FirebaseRealtimeProvider = ({
  children,
  experienceId,
  userId,
}: {
  children: React.ReactNode;
  experienceId?: string;
  userId?: string;
}) => {
  const appRegion = useAppRegion();
  const currentUser = useCurrentUser();
  // APL-12040: null-safe reads — logout nulls the auth user while this
  // provider is still mounted (Diagnostic Tool report); teardown must not
  // throw into the root error boundary.
  const testId = currentUser?.role === 'student' ? experienceId : undefined;
  const blockOnStudentError = currentUser?.role === 'student';
  // const userId = currentUser.id;
  const userUsername = currentUser?.email;
  const { firebaseUser, loading, error, retry, firebaseServices } = useFirebaseUser({
    providedToken: null,
    username: userUsername,
    extraParams: {
      region: 'global',
      test_id: testId,
      user_id: userId,
      username: userUsername,
    },
    // presenceData: {
    //   resource: `experiences/${testId}/users/${userId}`,
    //   meta: {
    //     isOnline,
    //   },
    // },
  });

  const { isFirebaseUserOnline } = useGetFirebaseOnlineStatus({
    firebaseServices,
  });

  useSetFirebaseUserPresence({
    user: firebaseUser,
    resource: testId && userId ? `experiences/${testId}/users/${userId}` : '',
    firebaseServices,
  });

  const finalValue: {
    appRegion: string;
    realtimeClient: FirebaseServices | null;
    user: UserCredential['user'] | null;
    isFirebaseUserOnline: boolean;
  } = useMemo(() => {
    return {
      appRegion,
      realtimeClient: firebaseServices,
      user: firebaseUser,
      isFirebaseUserOnline,
    };
  }, [appRegion, firebaseServices, firebaseUser, isFirebaseUserOnline]);

  return (
    <FirebaseRealtimeContext.Provider value={finalValue}>
      {(() => {
        if (loading) {
          return <RealtimePendingFallback />;
        }
        if (error && blockOnStudentError) {
          // FS-03 page for a native Firestore sign-in failure (APL-11641); every
          // other error keeps the FS-01 connection page.
          return (
            <RealtimeDBError
              errorType={
                error?.name === 'NativeFirestoreSignInError' ? 'native_connection' : 'connection'
              }
            />
          );
        }
        return children;
      })()}
    </FirebaseRealtimeContext.Provider>
  );
};

export const useSupabaseUser = ({
  providedToken,
  username,
  extraParams,
}: {
  providedToken: string | null;
  extraParams?: any;
  username?: string;
}) => {
  // const [supabaseUser, setSupabaseUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  const [supabaseServices, setSupabaseServices] = useState<SupabaseServices | null>(null);
  const enabledFeatures = useEnabledFeatures();

  const stringifiedExtraParams = JSON.stringify(extraParams);

  useEffect(() => {
    setIsLoading(true);

    const initializeSupabase = async (token: string | null) => {
      try {
        // Fetch token if not provided
        const authToken = token || (await getRealtimeDbToken(extraParams, enabledFeatures));

        // Initialize Supabase services with the token
        const services = initializeSupabaseApp(authToken);

        setSupabaseServices(services);
        setError(null);
      } catch (err: any) {
        console.error('Error initializing Supabase:', err);
        Sentry.captureException(err);
        setError(err);
        setSupabaseServices(null);
      } finally {
        setIsLoading(false);
      }
    };

    initializeSupabase(providedToken);

    return () => {
      if (supabaseServices) {
        supabaseServices.auth.signOut();
      }
    };
  }, [providedToken, retryCount, stringifiedExtraParams, username]);

  const retry = () => {
    setRetryCount((prev) => prev + 1);
  };

  return { supabaseUser: null, loading: isLoading, error, retry, supabaseServices };
};

export const SupabaseRealtimeContext = createContext({});

const SupabaseRealtimeProvider = ({
  children,
  experienceId,
  userId,
}: {
  children: React.ReactNode;
  experienceId?: string;
  userId?: string;
}) => {
  const appRegion = useAppRegion();
  const currentUser = useCurrentUser();
  // APL-12040: null-safe reads — same logout-teardown window as the
  // Firebase provider above; a null user must not hard-crash the tree.
  const testId = currentUser?.role === 'student' ? experienceId : undefined;
  const isStudent = currentUser?.role === 'student';
  const userUsername = currentUser?.email;

  const { supabaseUser, loading, error, retry, supabaseServices } = useSupabaseUser({
    providedToken: null,
    username: userUsername,
    extraParams: {
      // In case of supabase provider, we need to set the region to china for now. Not getting from ENV is because we need to support supabase in global region as well with flag.
      region: 'china',
      test_id: testId,
      user_id: userId,
      username: userUsername,
    },
  });
  console.log('check presence resource ====>', testId);

  useSetSupabaseUserPresence({
    // Null user (logout teardown): userId goes undefined and the presence
    // effect's `!userId` guard makes the subscription a no-op.
    userId: currentUser?.id,
    resource: testId ? `experiences/${testId}/users` : ``,
    meta: {
      presenceKey: String(currentUser?.id),
      presencePayload: {
        user_id: currentUser?.id,
        experience_id: testId,
        email: currentUser?.email,
      },
    },
    supabaseServices,
  });

  const { isOnline } = useIsOnline();
  const prevIsOnlineRef = useRef(isOnline);

  useEffect(() => {
    if (!prevIsOnlineRef.current && isOnline && supabaseServices) {
      supabaseServices.client.realtime.onHeartbeat((status) => {
        if (status === 'disconnected' || status === 'error') {
          supabaseServices.client.realtime.connect();
        }
      });
    }
    prevIsOnlineRef.current = isOnline;
  }, [isOnline, supabaseServices]);

  const finalValue: {
    appRegion: string;
    realtimeClient: SupabaseServices | null;
    user: User | null;
  } = useMemo(() => {
    return {
      appRegion,
      realtimeClient: supabaseServices,
      user: supabaseUser,
    };
  }, [appRegion, supabaseServices, supabaseUser]);

  const renderChildren = () => {
    if (loading) {
      return <RealtimePendingFallback />;
    }
    if (error && isStudent) {
      return <RealtimeDBError errorType="connection" />;
    }
    return children;
  };

  return (
    <SupabaseRealtimeContext.Provider value={finalValue}>
      {renderChildren()}
    </SupabaseRealtimeContext.Provider>
  );
};

export const RealtimeProvider = ({
  children,
  experienceId,
  userId,
}: {
  dataProviders?: any;
  children: React.ReactNode;
  experienceId?: string;
  userId?: string;
}) => {
  const dataProviderName = useRealtimeDbProviderName();
  if (dataProviderName === 'supabase') {
    return (
      <SupabaseRealtimeProvider experienceId={experienceId} userId={userId}>
        {children}
      </SupabaseRealtimeProvider>
    );
  } else {
    return (
      <FirebaseRealtimeProvider experienceId={experienceId} userId={userId}>
        {children}
      </FirebaseRealtimeProvider>
    );
  }
};
